/**
 * LT-17 §4 — Client Estimate tab.
 *
 * Replaces the LT-16-Part-4a "coming soon" stub with the tender's
 * actual client-facing document. State machine, top-down priority:
 *
 *   1. At least one sent version → show it. Iframe-embed the stored
 *      PDF from GridFS (`GET /tenders/{id}/versions/{ref}/pdf`). This
 *      is the exact document the client is holding — not a re-render.
 *      Older sent versions collapse under a "History" link.
 *
 *   2. No sent version, but a Prepared version exists → embed the
 *      Prepared PDF, with a "PREPARED — awaiting Mark as Sent" banner
 *      overlaid.
 *
 *   3. Nothing issued → embed the live preview PDF
 *      (`GET /tenders/{id}/preview.pdf`), with a "PREVIEW — NOT
 *      ISSUED" watermark and a status line naming the next action:
 *        - N pre-send blockers unresolved → link to the tab holding them
 *        - Blockers clear, no version → "Pending — create the client
 *          estimate from the Adjudication tab"
 *        - (Prepared case handled above)
 *
 * The status line is never generic; a "coming soon" is what caused
 * the Chris feedback that shipped features looked unbuilt.
 */
import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, ExternalLink, FileText, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card } from "../../components/UI";
import { gbp, ukDate } from "../../lib/format";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export function ClientEstimateTab({ tender }) {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    let dropped = false;
    setState({ loading: true });
    Promise.all([
      api.get(`/tenders/${tender.id}/versions`),
      api.get(`/tenders/${tender.id}/versions/prepared`),
      api.get(`/tenders/${tender.id}/overview`),
    ])
      .then(([vRes, pRes, oRes]) => {
        if (dropped) return;
        const versions = vRes.data?.versions || [];
        const prepared = pRes.data?.prepared || null;
        const isStale = !!pRes.data?.is_stale;
        const blockers = oRes.data?.pre_send_checks?.blockers || [];
        const blocking = blockers.filter((b) => b.severity === "block");
        setState({ loading: false, versions, prepared, isStale, blocking });
      })
      .catch(() => {
        if (dropped) return;
        toast.error("Failed to load client estimate state");
        setState({ loading: false, versions: [], prepared: null, isStale: false, blocking: [] });
      });
    return () => { dropped = true; };
  }, [tender.id, tender.updated_at, tender.status, tender.current_version]);

  const latestSent = state.versions?.[0] || null;   // list is newest-first
  const olderSent  = (state.versions || []).slice(1);

  const pdfUrl = (ref) =>
    `${BACKEND}/api/tenders/${tender.id}/versions/${ref}/pdf`;
  const previewUrl =
    `${BACKEND}/api/tenders/${tender.id}/preview.pdf?_t=${encodeURIComponent(tender.updated_at || "")}`;

  const statusLine = useMemo(() => {
    if (state.loading) return { tone: "info", text: "Loading…" };
    if (latestSent) return null;
    if (state.prepared && !state.isStale) return {
      tone: "prepared",
      text: `Prepared — awaiting Mark as Sent (${state.prepared.version_ref}, prepared ${ukDate(state.prepared.issued_at)}).`,
      cta: null,
    };
    if (state.prepared && state.isStale) return {
      tone: "warn",
      text: `Prepared version ${state.prepared.version_ref} was invalidated by an edit after generation. Re-create the client estimate before it can be marked sent.`,
      cta: { label: "Go to Adjudication", tab: "adjudication" },
    };
    if ((state.blocking || []).length > 0) return {
      tone: "block",
      text: `${state.blocking.length} pre-send blocker${state.blocking.length === 1 ? "" : "s"} unresolved — resolve on the Estimate tab (or Adjudication for A&O / margin) before issuing.`,
      cta: { label: "Go to Estimate", tab: "estimate" },
    };
    return {
      tone: "pending",
      text: "Pending — create the client estimate from the Adjudication tab.",
      cta: { label: "Go to Adjudication", tab: "adjudication" },
    };
  }, [state, latestSent]);

  if (state.loading) {
    return (
      <div className="mt-6 flex items-center gap-2 text-ink-3" style={{ fontSize: 12 }} data-testid="client-estimate-tab">
        <Loader2 size={13} className="animate-spin" /> Loading client estimate…
      </div>
    );
  }

  // === 1. Sent version exists ==========================================
  if (latestSent) {
    return (
      <div className="mt-6 space-y-4" data-testid="client-estimate-tab">
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-ink" style={{ fontSize: 15, fontWeight: 600 }}>
                Client Estimate — <span className="font-mono">{latestSent.version_ref}</span>
              </h3>
              <p className="text-ink-3 mt-1" style={{ fontSize: 12 }}>
                Sent <span className="font-mono tnum">{ukDate(latestSent.sent_at)}</span>
                {" · "}Client total <span className="font-mono tnum">{gbp(latestSent.client_total)}</span>
                {" · "}This is the exact PDF the client received.
              </p>
            </div>
            <a
              href={pdfUrl(latestSent.version_ref)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2"
              style={{ fontSize: 12 }}
              data-testid="client-estimate-open-pdf"
            >
              <ExternalLink size={12} /> Open in new tab
            </a>
          </div>
          <div className="mt-4 rounded-md border border-line overflow-hidden" style={{ background: "#F7F5EE" }}>
            <iframe
              src={pdfUrl(latestSent.version_ref)}
              title={`Client Estimate ${latestSent.version_ref}`}
              className="w-full"
              style={{ height: 780, border: 0 }}
              data-testid="client-estimate-iframe"
            />
          </div>
        </Card>

        {olderSent.length > 0 && (
          <Card className="p-4">
            <button
              onClick={() => setShowHistory((s) => !s)}
              className="w-full flex items-center justify-between text-ink-2 hover:text-ink"
              style={{ fontSize: 12.5 }}
              data-testid="client-estimate-history-toggle"
            >
              <span>
                <FileText size={12} className="inline mr-1.5 -mt-0.5 text-ink-3" />
                Older sent versions ({olderSent.length})
              </span>
              <span className="text-ink-3">{showHistory ? "Hide" : "Show"}</span>
            </button>
            {showHistory && (
              <ul className="mt-3 space-y-1">
                {olderSent.map((v) => (
                  <li key={v.id} className="flex items-center justify-between py-1.5 border-t border-line-soft" style={{ fontSize: 12 }}>
                    <span className="font-mono text-ink-2">{v.version_ref}</span>
                    <span className="font-mono tnum text-ink-3">Sent {ukDate(v.sent_at)}</span>
                    <span className="font-mono tnum text-ink-2">{gbp(v.client_total)}</span>
                    <a
                      href={pdfUrl(v.version_ref)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-accent hover:text-accent-deep inline-flex items-center gap-1"
                      style={{ fontSize: 11.5 }}
                    >
                      Open <ExternalLink size={10} />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
      </div>
    );
  }

  // === 2 + 3. No sent version — banner + iframe over live preview ======
  const bannerLabel = state.prepared ? "PREPARED — NOT SENT" : "PREVIEW — NOT ISSUED";
  const bannerTone  = state.prepared ? "prepared" : "preview";
  const iframeSrc   = state.prepared ? pdfUrl(state.prepared.version_ref) : previewUrl;
  const showIframe  = state.prepared || (state.blocking || []).length === 0;

  return (
    <div className="mt-6 space-y-4" data-testid="client-estimate-tab">
      <Card className="p-5">
        <h3 className="font-display text-ink" style={{ fontSize: 15, fontWeight: 600 }}>
          Client Estimate
        </h3>
        <p className="text-ink-3 mt-1" style={{ fontSize: 12 }}>
          {state.prepared
            ? "A Prepared version has been generated but not yet marked sent. This is what the client will receive when you Mark as Sent."
            : "Nothing has been issued yet. Preview below reflects the current tender state; it is not a version and will regenerate on every edit."}
        </p>

        {statusLine && (
          <div
            className="mt-4 rounded-md border px-4 py-3 flex items-start justify-between gap-3"
            style={{
              background:  statusLine.tone === "block"    ? "#FBECEC"
                        :  statusLine.tone === "warn"     ? "#FEF3E2"
                        :  statusLine.tone === "prepared" ? "#EFF4EE"
                        :                                    "#F0EEE6",
              borderColor: statusLine.tone === "block"    ? "#E9C0C0"
                        :  statusLine.tone === "warn"     ? "#F2DFB8"
                        :  statusLine.tone === "prepared" ? "#D6E2D8"
                        :                                    "#E2DDD2",
              color:       statusLine.tone === "block"    ? "#7A1F1F"
                        :  statusLine.tone === "warn"     ? "#7A4C0A"
                        :  statusLine.tone === "prepared" ? "#2F4F3E"
                        :                                    "#5A4A2A",
            }}
            data-testid="client-estimate-status-line"
          >
            <span style={{ fontSize: 12.5 }}>
              <AlertTriangle size={12} className="inline -mt-0.5 mr-1.5" />
              {statusLine.text}
            </span>
            {statusLine.cta && (
              <button
                onClick={() => nav(`/tenders/${tender.id}?tab=${statusLine.cta.tab}`)}
                className="inline-flex items-center gap-1 font-medium hover:underline"
                style={{ fontSize: 12.5 }}
                data-testid="client-estimate-status-cta"
              >
                {statusLine.cta.label} <ArrowRight size={11} />
              </button>
            )}
          </div>
        )}

        {showIframe && (
          <div className="mt-4 relative rounded-md border border-line overflow-hidden" style={{ background: "#F7F5EE" }}>
            <iframe
              src={iframeSrc}
              title="Client Estimate preview"
              className="w-full"
              style={{ height: 780, border: 0 }}
              data-testid="client-estimate-iframe"
            />
            {/* CSS-only diagonal watermark; the renderer is locked
                design so we overlay on top of the iframe. Pointer
                events disabled so the PDF still scrolls. */}
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              aria-hidden="true"
              data-testid="client-estimate-watermark"
            >
              <span
                style={{
                  transform: "rotate(-24deg)",
                  fontSize: 68,
                  fontWeight: 900,
                  letterSpacing: "0.14em",
                  color: bannerTone === "prepared" ? "rgba(47, 79, 62, 0.14)" : "rgba(122, 76, 10, 0.16)",
                  border: `4px solid ${bannerTone === "prepared" ? "rgba(47, 79, 62, 0.14)" : "rgba(122, 76, 10, 0.16)"}`,
                  padding: "12px 32px",
                  borderRadius: 8,
                  fontFamily: "monospace",
                }}
              >
                {bannerLabel}
              </span>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
